import { Project, MonthlyProjectOverrides, ResolvedMonthlyData } from '../types/project';

/**
 * Resolves monthly data by merging base template with monthly overrides
 */
export function resolveMonthlyData(
  project: Project, 
  month: string
): ResolvedMonthlyData {
  const overrides = project.monthlyOverrides?.[month];
  const base = project.baseTemplate;

  return {
    month,
    revenue: overrides?.revenue ?? base.revenue,
    profitMargin: overrides?.profitMargin ?? base.profitMargin,
    operationsOverheadPercentage: overrides?.operationsOverheadPercentage ?? base.operationsOverheadPercentage,
    operationsCompensationPerOwner: overrides?.operationsCompensationPerOwner ?? (base.operationsCompensationPerOwner || 2500),
    strategySponsor: overrides?.strategySponsor ?? base.strategySponsor,
    operationsOwners: overrides?.operationsOwners ?? base.operationsOwners,
    tacticalSpecialists: overrides?.tacticalSpecialists ?? base.tacticalSpecialists,
    performanceKickers: overrides?.performanceKickers ?? base.performanceKickers,
    isInherited: {
      revenue: overrides?.revenue === undefined,
      profitMargin: overrides?.profitMargin === undefined,
      operationsOverheadPercentage: overrides?.operationsOverheadPercentage === undefined,
      operationsCompensationPerOwner: overrides?.operationsCompensationPerOwner === undefined,
      strategySponsor: overrides?.strategySponsor === undefined,
      operationsOwners: overrides?.operationsOwners === undefined,
      tacticalSpecialists: overrides?.tacticalSpecialists === undefined,
      performanceKickers: overrides?.performanceKickers === undefined,
    }
  };
}

/**
 * Creates monthly overrides from resolved data
 */
export function createMonthlyOverrides(
  project: Project,
  month: string,
  data: Partial<ResolvedMonthlyData>
): MonthlyProjectOverrides {
  const base = project.baseTemplate;
  const existingOverrides = project.monthlyOverrides?.[month];
  
  const overrides: MonthlyProjectOverrides = {
    month,
    createdAt: existingOverrides?.createdAt || new Date(),
    updatedAt: new Date()
  };

  console.log('=== CREATE OVERRIDES DEBUG ===');
  console.log('Base template:', base);
  console.log('Input data:', data);
  console.log('Existing overrides:', existingOverrides);

  // Only include fields that are different from base template
  if (data.revenue !== undefined && data.revenue !== base.revenue) {
    overrides.revenue = data.revenue;
    console.log(`Revenue override: ${data.revenue} (base: ${base.revenue})`);
  }
  if (data.profitMargin !== undefined && data.profitMargin !== base.profitMargin) {
    overrides.profitMargin = data.profitMargin;
    console.log(`Profit margin override: ${data.profitMargin} (base: ${base.profitMargin})`);
  }
  if (data.operationsOverheadPercentage !== undefined && data.operationsOverheadPercentage !== base.operationsOverheadPercentage) {
    overrides.operationsOverheadPercentage = data.operationsOverheadPercentage;
    console.log(`Operations overhead override: ${data.operationsOverheadPercentage} (base: ${base.operationsOverheadPercentage})`);
  }
  if (data.operationsCompensationPerOwner !== undefined && data.operationsCompensationPerOwner !== (base.operationsCompensationPerOwner || 2500)) {
    overrides.operationsCompensationPerOwner = data.operationsCompensationPerOwner;
    console.log(`Operations compensation override: ${data.operationsCompensationPerOwner} (base: ${base.operationsCompensationPerOwner || 2500})`);
  }
  if (data.strategySponsor !== undefined && data.strategySponsor !== base.strategySponsor) {
    overrides.strategySponsor = data.strategySponsor;
    console.log(`Strategy sponsor override: ${data.strategySponsor} (base: ${base.strategySponsor})`);
  }
  if (data.operationsOwners !== undefined && !arraysEqual(data.operationsOwners, base.operationsOwners)) {
    overrides.operationsOwners = data.operationsOwners;
    console.log(`Operations owners override:`, data.operationsOwners, '(base:', base.operationsOwners, ')');
  }
  if (data.tacticalSpecialists !== undefined && !arraysEqual(data.tacticalSpecialists, base.tacticalSpecialists)) {
    overrides.tacticalSpecialists = data.tacticalSpecialists;
    console.log(`Tactical specialists override:`, data.tacticalSpecialists, '(base:', base.tacticalSpecialists, ')');
  }
  if (data.performanceKickers !== undefined && !arraysEqual(data.performanceKickers, base.performanceKickers)) {
    overrides.performanceKickers = data.performanceKickers;
    console.log(`Performance kickers override:`, data.performanceKickers, '(base:', base.performanceKickers, ')');
  }

  console.log('Final overrides:', overrides);
  console.log('===============================');

  return overrides;
}

/**
 * Updates the base template with values from monthly data
 */
export function updateBaseTemplateFromMonth(
  project: Project,
  resolvedData: ResolvedMonthlyData
): Project {
  return {
    ...project,
    baseTemplate: {
      revenue: resolvedData.revenue,
      profitMargin: resolvedData.profitMargin,
      operationsOverheadPercentage: resolvedData.operationsOverheadPercentage,
      operationsCompensationPerOwner: resolvedData.operationsCompensationPerOwner,
      strategySponsor: resolvedData.strategySponsor,
      operationsOwners: resolvedData.operationsOwners,
      tacticalSpecialists: resolvedData.tacticalSpecialists,
      performanceKickers: resolvedData.performanceKickers,
    },
    updatedAt: new Date()
  };
}

/**
 * Helper function to compare arrays
 */
function arraysEqual(a: any[], b: any[]): boolean {
  if (a.length !== b.length) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Gets all months that have data for a project
 */
export function getProjectMonths(project: Project): string[] {
  return Object.keys(project.monthlyOverrides || {}).sort();
}

/**
 * Checks if a project has any data for a specific month
 */
export function hasMonthlyData(project: Project, month: string): boolean {
  return project.monthlyOverrides?.[month] !== undefined;
}

/**
 * Migrates old project format to new template-based format
 */
export function migrateProjectToTemplate(oldProject: any): Project {
  // If it's already in the new format, return as-is
  if (oldProject.baseTemplate) {
    return oldProject;
  }

  // Migration logic for old format
  const firstMonthData = oldProject.monthlyData ? Object.values(oldProject.monthlyData)[0] as any : null;
  
  return {
    id: oldProject.id,
    projectName: oldProject.projectName,
    status: oldProject.status || 'active', // Default to active for existing projects
    baseTemplate: firstMonthData ? {
      revenue: firstMonthData.revenue || 0,
      profitMargin: firstMonthData.profitMargin || 50,
      operationsOverheadPercentage: firstMonthData.operationsOverheadPercentage || 10,
      operationsCompensationPerOwner: firstMonthData.operationsCompensationPerOwner || 2500,
      strategySponsor: firstMonthData.strategySponsor || 'Ken',
      operationsOwners: firstMonthData.operationsOwners || ['Ken'],
      tacticalSpecialists: firstMonthData.tacticalSpecialists || [],
      performanceKickers: firstMonthData.performanceKickers || [],
    } : {
      revenue: 0,
      profitMargin: 50,
      operationsOverheadPercentage: 10,
      operationsCompensationPerOwner: 2500,
      strategySponsor: 'Ken',
      operationsOwners: ['Ken'],
      tacticalSpecialists: [],
      performanceKickers: [],
    },
    monthlyOverrides: {},
    createdAt: oldProject.createdAt,
    updatedAt: oldProject.updatedAt
  };
}